import React, { useState, useEffect } from 'react';
import { addCsrfTokenToConfig } from '../../../utils/csrfUtils';
import Card from '../../common/Card';
import Button from '../../common/Button';
import FormInput from '../../common/FormInput';
import Modal from '../../common/Modal';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import ConfirmDialog from '../../common/ConfirmDialog';
import UserCard from '../UserCard';
import UserForm from '../UserForm';
import './styles.css';

/**
 * 用戶管理組件
 * 用於管理系統用戶
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.token - 認證令牌
 * @returns {JSX.Element} 用戶管理組件
 */
function UserManagement({ token }) {
    // 用戶列表狀態
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // 新增用戶狀態
    const [showAddModal, setShowAddModal] = useState(false);
    const [addMessage, setAddMessage] = useState('');
    const [addMessageType, setAddMessageType] = useState('');
    const [addLoading, setAddLoading] = useState(false);

    // 編輯用戶狀態
    const [showEditModal, setShowEditModal] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [editMessage, setEditMessage] = useState('');
    const [editMessageType, setEditMessageType] = useState('');
    const [editLoading, setEditLoading] = useState(false);

    // 刪除用戶狀態
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteUserId, setDeleteUserId] = useState(null);

    // 獲取用戶列表
    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            };

            // 添加CSRF令牌
            const configWithCsrf = addCsrfTokenToConfig(config);

            const res = await fetch('/api/users', {
                headers: configWithCsrf.headers
            });

            if (res.ok) {
                const data = await res.json();
                setUsers(data);
            } else {
                const errorData = await res.json();
                setError(errorData.error || '獲取用戶列表失敗');
            }
        } catch (error) {
            setError('獲取用戶列表失敗: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // 初始加載
    useEffect(() => {
        fetchUsers();
    }, [token]);

    // 處理新增用戶
    const handleAddUser = async (userData) => {
        try {
            setAddLoading(true);
            setAddMessage('');
            setAddMessageType('');

            const config = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(userData)
            };

            // 添加CSRF令牌
            const configWithCsrf = addCsrfTokenToConfig(config);

            const res = await fetch('/api/users', {
                method: configWithCsrf.method,
                headers: configWithCsrf.headers,
                body: configWithCsrf.body
            });

            if (res.ok) {
                setAddMessage('用戶創建成功');
                setAddMessageType('success');
                fetchUsers();

                // 延遲關閉模態框
                setTimeout(() => {
                    closeAddModal();
                }, 1500);
            } else {
                const errorData = await res.json();
                setAddMessage(errorData.error || '創建用戶失敗');
                setAddMessageType('error');
            }
        } catch (error) {
            setAddMessage('創建用戶失敗: ' + error.message);
            setAddMessageType('error');
        } finally {
            setAddLoading(false);
        }
    };

    // 處理編輯用戶
    const handleEditUser = async (userData) => {
        try {
            setEditLoading(true);
            setEditMessage('');
            setEditMessageType('');

            const config = {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(userData)
            };

            // 添加CSRF令牌
            const configWithCsrf = addCsrfTokenToConfig(config);

            const res = await fetch('/api/users', {
                method: configWithCsrf.method,
                headers: configWithCsrf.headers,
                body: configWithCsrf.body
            });

            if (res.ok) {
                setEditMessage('用戶更新成功');
                setEditMessageType('success');
                fetchUsers();

                // 延遲關閉模態框
                setTimeout(() => {
                    closeEditModal();
                }, 1500);
            } else {
                const errorData = await res.json();
                setEditMessage(errorData.error || '更新用戶失敗');
                setEditMessageType('error');
            }
        } catch (error) {
            setEditMessage('更新用戶失敗: ' + error.message);
            setEditMessageType('error');
        } finally {
            setEditLoading(false);
        }
    };

    // 處理刪除用戶
    const handleDeleteUser = async () => {
        try {
            const config = {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            };

            // 添加CSRF令牌
            const configWithCsrf = addCsrfTokenToConfig(config);

            const res = await fetch(`/api/users/${deleteUserId}`, {
                method: configWithCsrf.method,
                headers: configWithCsrf.headers
            });

            if (res.ok) {
                setUsers(users.filter(user => user.id !== deleteUserId));
                setError(null);
            } else {
                const errorData = await res.json();
                setError(errorData.error || '刪除用戶失敗');
            }
        } catch (error) {
            setError('刪除用戶失敗: ' + error.message);
        } finally {
            setShowDeleteDialog(false);
            setDeleteUserId(null);
        }
    };

    // 打開編輯模態框
    const openEditModal = (user) => {
        setEditUser(user);
        setEditMessage('');
        setEditMessageType('');
        setShowEditModal(true);
    };

    // 打開刪除對話框
    const openDeleteDialog = (userId) => {
        setDeleteUserId(userId);
        setShowDeleteDialog(true);
    };

    // 關閉新增模態框
    const closeAddModal = () => {
        setShowAddModal(false);
        setAddMessage('');
        setAddMessageType('');
    };

    // 關閉編輯模態框
    const closeEditModal = () => {
        setShowEditModal(false);
        setEditUser(null);
        setEditMessage('');
        setEditMessageType('');
    };

    // 關閉刪除對話框
    const closeDeleteDialog = () => {
        setShowDeleteDialog(false);
        setDeleteUserId(null);
    };

    // 過濾用戶
    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // 加載中狀態
    if (loading) {
        return <Loading text="正在加載用戶數據..." />;
    }

    return (
        <div className="user-management-container">
            {/* 頁面標題 */}
            <div className="user-management-header">
                <h2 className="user-management-title">用戶管理</h2>
                <Button
                    variant="primary"
                    onClick={() => setShowAddModal(true)}
                >
                    新增用戶
                </Button>
            </div>

            {/* 錯誤信息 */}
            {error && (
                <ErrorMessage
                    message={error}
                    onRetry={fetchUsers}
                />
            )}

            {/* 搜索區域 */}
            <Card className="user-search-card">
                <div className="user-search-container">
                    <FormInput
                        id="searchTerm"
                        placeholder="搜索用戶..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="user-search-input"
                    />
                </div>
            </Card>

            {/* 用戶列表 */}
            <div className="user-grid">
                {filteredUsers.length === 0 ? (
                    <Card className="user-empty-card">
                        <div className="user-empty-message">
                            <p>暫無用戶數據</p>
                        </div>
                    </Card>
                ) : (
                    filteredUsers.map(user => (
                        <UserCard
                            key={user.id}
                            user={user}
                            onEdit={openEditModal}
                            onDelete={openDeleteDialog}
                        />
                    ))
                )}
            </div>

            {/* 新增用戶模態框 */}
            <Modal
                isOpen={showAddModal}
                onClose={closeAddModal}
                title="新增用戶"
                size="small"
            >
                <UserForm
                    onSubmit={handleAddUser}
                    onCancel={closeAddModal}
                    submitText="新增"
                    message={addMessage}
                    messageType={addMessageType}
                    loading={addLoading}
                />
            </Modal>

            {/* 編輯用戶模態框 */}
            <Modal
                isOpen={showEditModal}
                onClose={closeEditModal}
                title="編輯用戶"
                size="small"
            >
                <UserForm
                    user={editUser}
                    onSubmit={handleEditUser}
                    onCancel={closeEditModal}
                    submitText="更新"
                    message={editMessage}
                    messageType={editMessageType}
                    loading={editLoading}
                />
            </Modal>

            {/* 刪除確認對話框 */}
            <ConfirmDialog
                isOpen={showDeleteDialog}
                title="刪除用戶"
                message="確定要刪除此用戶嗎？此操作無法撤銷。"
                confirmText="刪除"
                cancelText="取消"
                onConfirm={handleDeleteUser}
                onCancel={closeDeleteDialog}
            />
        </div>
    );
}

export default UserManagement; 